import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileDown, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useLocation } from 'wouter';
import { SupplementRoomBreakdown } from './tabs/SupplementRoomBreakdown';
import { SupplementSummary } from './tabs/SupplementSummary';
import { useSupplementPdfExport, useSupplementExcelExport } from '@/hooks/use-supplements';
import { Badge } from '@/components/ui/badge';
import type { SupplementDetailed } from '@/api/generated/client/models';
import type { DisplayRoom, DisplayTotals } from '@/types/supplement';
import { SupplementAccessProvider } from './supplement-access-context';

interface SupplementTabsProps {
  supplement: SupplementDetailed;
  rooms: DisplayRoom[];
  totals: DisplayTotals;
  isLoading?: boolean;
  comparisonName?: string;
  claimNumber?: string;
  canWrite?: boolean;
}

/**
 * Main tabs component for supplement detail view
 * Consolidated component that includes header, navigation, and tab content
 * Follows the same pattern as ComparisonTabs
 */
export function SupplementTabs({
  supplement,
  rooms,
  totals,
  isLoading = false,
  comparisonName,
  claimNumber,
  canWrite = false,
}: SupplementTabsProps) {
  const [activeTab, setActiveTab] = useState('breakdown');
  const [, setLocation] = useLocation();

  // PDF export handler
  const { handlePdfExport, isPending: isPdfExporting } = useSupplementPdfExport();

  // Excel export handler
  const { handleExcelExport, isPending: isExcelExporting } = useSupplementExcelExport();

  const handleBackToClaim = () => {
    setLocation(`/claims/${supplement.claimId}`);
  };

  const totalMoneyFoundCents = rooms.reduce((sum, r) => sum + r.moneyFoundCents, 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">Loading supplement details...</div>
      </div>
    );
  }

  return (
    <SupplementAccessProvider value={canWrite}>
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-4 flex items-start gap-3 sm:items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBackToClaim}
            className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <h1 className="flex flex-wrap items-center gap-1.5 text-base font-bold leading-tight text-foreground sm:gap-2">
              {supplement.name}
              {supplement.state && (
                <Badge variant="outline" className="text-[10px] uppercase font-bold px-1.5 h-4 border-muted-foreground/30 text-muted-foreground">
                  {supplement.state}
                </Badge>
              )}
              <Badge
                variant={supplement.status === 'approved' ? 'default' : supplement.status === 'rejected' ? 'destructive' : 'secondary'}
                className="text-[10px] uppercase font-bold px-1.5 h-4"
              >
                {supplement.status}
              </Badge>
            </h1>
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-[9px] text-muted-foreground uppercase font-bold tracking-wider">
              <span>{comparisonName || `Comparison #${supplement.comparisonId}`}</span>
              <span>•</span>
              <span>Claim #{claimNumber || supplement.claimId}</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <TabsList className="grid h-auto w-full grid-cols-2 gap-1 rounded-lg border bg-white/50 p-1 shadow-xs sm:flex sm:w-fit sm:gap-0 sm:p-0.5 dark:bg-slate-900/50">
            <TabsTrigger value="breakdown" className="rounded-md px-2 py-2 text-xs font-medium data-[state=active]:bg-primary/10 data-[state=active]:text-primary sm:px-4 sm:py-1.5">
              Room Breakdown
            </TabsTrigger>
            <TabsTrigger value="summary" className="rounded-md px-2 py-2 text-xs font-medium data-[state=active]:bg-primary/10 data-[state=active]:text-primary sm:px-4 sm:py-1.5">
              Summary
            </TabsTrigger>
          </TabsList>

          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap">
            <Button
              onClick={() => handleExcelExport(supplement.id)}
              disabled={isExcelExporting}
              className="h-8 w-full gap-2 text-xs sm:h-auto sm:w-auto"
              variant="outline"
              size="sm"
            >
              {isExcelExporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <FileSpreadsheet className="h-4 w-4" />
                  Export Excel
                </>
              )}
            </Button>

            <Button
              onClick={() => handlePdfExport(supplement.id)}
              disabled={isPdfExporting}
              className="h-8 w-full gap-2 text-xs sm:h-auto sm:w-auto"
              variant="outline"
              size="sm"
            >
              {isPdfExporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Requesting...
                </>
              ) : (
                <>
                  <FileDown className="h-4 w-4" />
                  Export PDF
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <TabsContent value="breakdown" className="mt-4">
        <SupplementRoomBreakdown
          rooms={rooms}
          supplementId={supplement.id}
          totalSupplementCents={totals.totalSupplementCents}
          totalMoneyFoundCents={totalMoneyFoundCents}
        />
      </TabsContent>

      <TabsContent value="summary" className="mt-4">
        <SupplementSummary supplement={supplement} totals={totals} rooms={rooms} />
      </TabsContent>
    </Tabs>
    </SupplementAccessProvider>
  );
}
